import { getEventDef } from './events/registry.js';
import { toCanonicalEventName } from './events/naming.js';

/**
 * Notification ownership is deliberately separate from the automation catalog.
 * The catalog describes events that may exist; this registry describes delivery
 * ownership. Source inventory is evidence for these rows, never a promotion
 * mechanism.
 */
export const NOTIFICATION_OWNERS = Object.freeze([
    'policy',
    'code-owned',
    'catalog-only',
    'policy-blocked',
]);

const CRUD_ENTITIES = Object.freeze([
    'applicationMethod', 'assetCategory', 'ticketCategory', 'documentType', 'asset',
    'applicationRequestInformation', 'classification', 'transferType', 'retentionPeriod',
    'processingPurpose', 'assetGroup', 'assetLicense', 'assetDisposal', 'incident', 'incidentTemplate',
    'applicationTracking', 'dataSubjectRequest', 'assessment', 'assessmentQuestion',
    'auditAssignment', 'assignmentTemplate', 'integrationConnection', 'bcmInitialResponseLibrary',
    'bcmRpo', 'teamAssignment', 'teamDutyProfile', 'bia', 'bcp', 'changeRequest', 'serviceCatalogItem', 'reportConfidentialityClass',
    'collectionMethod', 'communication', 'competence', 'company', 'complianceFramework',
    'complianceObligation', 'context', 'control', 'crisisEvent', 'crisisRecoveryActivity',
    'document', 'dpiaTemplate', 'exercise', 'exerciseEvaluation', 'exerciseParticipant',
    'exerciseRecoveryActivity', 'formTemplate', 'explicitConsent', 'explicitConsentMethod',
    'explicitConsentReason', 'governanceReportDefinition', 'integrationConnector',
    'internationalTransfer', 'interestedParty', 'legalReason', 'meeting', 'objective', 'process',
    'problem', 'personalDataInventory', 'recoveryResources', 'regulatoryClause', 'regulatorySource',
    'riskMitigationDefinition', 'riskRegister', 'riskTreatment', 'rootCause', 'scenarioTimeline',
    'scopeDocument', 'subjectPersonGroup', 'team', 'threat', 'ticketTemplate', 'ticketSLA',
    'training', 'vulnerability', 'vendor', 'tenantDirectory',
    'bigdesRoadMapWorkPackage', 'bigdesCompensatoryControl', 'vendorAssessment', 'weakness',
    'cookieInventory', 'dataProcessingAgreement', 'disposalRun', 'dpiaAssessment', 'consentRecord',
    'privacyNotice', 'bcpLocation', 'dataBreach', 'role', 'unit', 'user', 'action',
    'biaRpoCalculation', 'biaRpoRecommendation',
    'kpi', 'strategicInitiative',
]);

const CRUD_PAGE_KEYS = Object.freeze({
    applicationMethod: 'definitionsApplicationTrackingApplicationMethod',
    assetCategory: 'assetInventoryAssetCategory',
    ticketCategory: 'ticketCategory',
    documentType: 'definitionsDocumentDocumentType',
    asset: 'assetInventoryAssets',
    applicationRequestInformation: 'definitionsApplicationTrackingApplicationRequestInformation',
    classification: 'definitionsDocumentClassification',
    transferType: 'definitionsKvkkTransferType',
    retentionPeriod: 'kvkkRetentionPeriod',
    processingPurpose: 'definitionsKvkkProcessingPurpose',
    assetGroup: 'assetInventoryAssetGroup',
    assetLicense: 'assetInventoryLicenseManagement',
    assetDisposal: 'assetInventoryDisposalManagement',
    incident: 'incidentManagementIncidents',
    incidentTemplate: 'incidentManagementIncidents',
    applicationTracking: 'gdprApplicationTracking',
    dataSubjectRequest: 'gdprDataSubjectRequest',
    assessment: 'internalAuditAssessment',
    assessmentQuestion: 'internalAuditAssessmentQuestions',
    auditAssignment: 'internalAuditMyAudits',
    assignmentTemplate: 'platformAssignmentTemplates',
    integrationConnection: 'platformThirdPartyApplications',
    bcmInitialResponseLibrary: 'bcmBcp', bcmRpo: 'bcmBia', teamAssignment: 'definitionsGeneralSystemTeam', teamDutyProfile: 'definitionsGeneralSystemTeam', dutyDefinition: 'definitionsGeneralSystemDutyDefinition', bia: 'bcmBia', bcp: 'bcmBcp',
    changeRequest: 'itsmChangeRequest', serviceCatalogItem: 'itsmServiceCatalog',
    reportConfidentialityClass: 'definitionsReportDesign', collectionMethod: 'definitionsKvkkCollectionMethod',
    communication: 'isoCommunication', competence: 'isoCompetence', company: 'definitionsKvkkCompany',
    complianceFramework: 'definitionsComplianceFrameworks', complianceObligation: 'definitionsComplianceObligations',
    context: 'isoContext', control: 'internalControlControl', crisisEvent: 'bcmCrisisEvent',
    crisisRecoveryActivity: 'bcmCrisisEvent', document: 'reportDocument', dpiaTemplate: 'definitionsPrivacyDpiaTemplates',
    exercise: 'bcmExercises', exerciseEvaluation: 'bcmExercises', exerciseParticipant: 'bcmExercises',
    exerciseRecoveryActivity: 'bcmExercises', formTemplate: 'definitionsSurveys', explicitConsent: 'gdprObtainingExplicitConsent',
    explicitConsentMethod: 'definitionsObtainingExplicitConsentMethod', explicitConsentReason: 'definitionsObtainingExplicitConsentReason',
    governanceReportDefinition: 'definitionsGovernanceReports', integrationConnector: 'definitionsIntegrationConnectors',
    internationalTransfer: 'definitionsKvkkInternationalTransfer', interestedParty: 'contextManagementInterestedParties',
    legalReason: 'definitionsKvkkLegalReason', meeting: 'isoMeeting', objective: 'isoObjective',
    kpi: 'isoObjective', strategicInitiative: 'isoObjective', process: 'definitionsGeneralSystemProcess',
    problem: 'itsmProblem', personalDataInventory: 'assetInventoryPdInventory', recoveryResources: 'bcmCrisisEvent',
    regulatoryClause: 'definitionsComplianceClauses', regulatorySource: 'definitionsComplianceRegulatorySources',
    riskMitigationDefinition: 'riskOperationsTreatment', riskRegister: 'riskOperationsRiskRegister', riskTreatment: 'riskOperationsTreatment',
    rootCause: 'definitionsGeneralSystemRootCause', scenarioTimeline: 'bcmExercises', scopeDocument: 'contextManagementScopeDocument',
    subjectPersonGroup: 'definitionsKvkkSubjectPersonGroup', team: 'definitionsGeneralSystemTeam', threat: 'ismsControlsThreatIntelligence',
    ticketTemplate: 'ticketTemplate', ticketSLA: 'ticketSLA', training: 'isoTraining',
    vulnerability: 'ismsControlsVulnerability', vendor: 'vendorManagementVendors', tenantDirectory: 'definitionsTenantDirectory',
    bigdesRoadMapWorkPackage: 'bigdesRoadMap', bigdesCompensatoryControl: 'bigdesCompensatoryControl', vendorAssessment: 'vendorManagementAssessments',
    weakness: 'ismsControlsVulnerability', cookieInventory: 'gdprCookieInventory', dataProcessingAgreement: 'gdprDpa', disposalRun: 'gdprDisposal',
    dpiaAssessment: 'gdprDpiaAssessment', consentRecord: 'gdprConsentRecord', privacyNotice: 'gdprPrivacyNotice', bcpLocation: 'bcmBcp',
    dataBreach: 'gdprDataBreach', role: 'userOperationsRoleManagement', unit: 'definitionsGeneralSystemUnit', user: 'userOperationsUserManagement',
    action: 'incidentActions', biaRpoCalculation: 'bcmBia', biaRpoRecommendation: 'bcmBia',
});

const SPECIALTY_POLICY_EVENTS = Object.freeze([
    'dataSubjectRequest.deadlineReminder',
    'dataSubjectRequest.deadlineOverdue',
    'dataBreach.deadlineReminder',
    'dataBreach.deadlineOverdue',
    'dataBreach.dataSubjectDeadlineReminder',
    'dataBreach.dataSubjectDeadlineOverdue',
    'disposalRun.periodicDue',
    'disposalRun.recordsExpired',
    'dataProcessingAgreement.deadlineReminder',
    'dataProcessingAgreement.expired',
    'dataProcessingAgreement.subProcessorAdded',
    'dataProcessingAgreement.subProcessorDecided',
    'gdpr.dataBreach.notified',
    'internationalTransfer.sscDeadlineReminder',
    'internationalTransfer.sscDeadlineOverdue',
    'ticket.pendingDeleteApproved',
    'ticket.pendingDeleteRejected',
    'ticket.pendingStatusRejected',
    'incident.pendingDeleteApproved',
    'incident.pendingDeleteRejected',
    'incident.pendingStatusRejected',
    'asset.pendingDeleteApproved',
    'asset.pendingDeleteRejected',
    'formTask.assigned',
    'collectionAssignment.assigned',
    'collectionAssignment.completed',
    'collectionAssignment.cancelled',
    'collectionAssignment.reopened',
    'collectionAssignment.reminded',
    'collectionAssignment.deadlineWarning',
    'collectionAssignment.overdue',
    'collectionCampaign.launched',
    'collectionCampaign.cancelled',
    'workflow.task.assigned',
    'workflow.task.slaWarning',
    'workflow.task.slaBreached',
    'workflow.task.slaEscalated',
    'workflow.informed.assigned',
    'role.assigned',
    'role.permissionsChanged',
    'ticket.assigned',
    'ticket.resolved',
    'ticket.closed',
    'ticket.submitted',
    'ticket.cancelled',
    'ticket.approved',
    'ticket.rejected',
    'ticket.returned',
    'ticket.delegated',
    'ticket.slaBreached',
    'ticket.slaWarning',
    'ticket.escalated',
    'ticket.commented',
    'bia.submitted',
    'bia.completed',
    'bcp.submitted',
    'bcp.activated',
    'crisisEvent.declared',
    'crisisEvent.resolved',
    'incident.submitted',
    'incident.cancelled',
    'incident.assigned',
    'incident.resolved',
    'incident.closed',
    'incident.slaBreached',
    'incident.approved',
    'incident.rejected',
    'incident.returned',
    'incident.delegated',
    'training.assigned',
    'training.completed',
    'training.overdue',
    'operationalRun.started',
    'operationalRun.paused',
    'operationalRun.resumed',
    'operationalRun.completed',
    'operationalRun.cancelled',
    'operationalRun.update.appended',
    'operationalRun.task.assigned',
    'operationalRun.task.started',
    'operationalRun.task.completed',
    'operationalRun.task.cancelled',
    'operationalRun.task.reassigned',
    'operationalRun.task.evidenceUploaded',
    'operationalRun.task.dependenciesOverridden',
    'operationalRun.task.deadlineWarning',
    'operationalRun.task.overdue',
]);

const CODE_OWNED_EXACT = Object.freeze([
    'workflow.notify',
]);

const CATALOG_ONLY_EXACT = Object.freeze([
    'assessment.completed',
    'risk.statusChanged',
    'document.approved',
    'document.rejected',
    // Automation/catalog twin of dataBreach.created — policy notify is dataBreach.created.
    'gdpr.dataBreach.detected',
]);

const POLICY_BLOCKED_EXACT = Object.freeze([]);

const DETAIL_PRODUCER_SOURCES = Object.freeze({
    Asset: 'apps/api/src/controllers/asset.controller.js:runAssetDetailAssociation',
    Bcp: 'apps/api/src/controllers/bcp.controller.js:runBcpDetailAssociation',
    Bia: 'apps/api/src/controllers/bia.controller.js:runBiaDetailAssociation',
    CrisisEvent: 'apps/api/src/controllers/crisis-event.controller.js:runCrisisEventDetailAssociation',
    Exercise: 'apps/api/src/controllers/exercise.controller.js:runExerciseDetailAssociation',
    RiskRegister: 'apps/api/src/controllers/risk-register.controller.js:runRiskDetailAssociation',
    Objective: 'apps/api/src/controllers/objective.controller.js:runObjectiveDetailAssociation',
});

const DETAIL_FAMILY_ROWS = Object.freeze([
    ...['risk', 'threat', 'control'].flatMap((detail) => ['linked', 'unlinked'].map((verb) => [
        `asset.${detail}.${verb}`, 'Asset', 'assetInventoryAssets', detail === 'control' ? 'controls' : `${detail}s`,
    ])),
    ...[
        ['process', 'relatedProcesses'],
        ['asset', 'relatedAssets'],
        ['vendor', 'relatedVendors'],
        ['document', 'documents'],
        ['team', 'roles'],
        ['initialResponse', 'initialResponse'],
        ['exercise', 'relatedExercises'],
        ['bia', 'relatedBias'],
        ['communication', 'communications'],
        ['location', 'locations', 'apps/api/src/controllers/bcp-location.controller.js'],
    ].flatMap(([detail, tab, producerSource]) => ['linked', 'unlinked'].map((verb) => [
        `bcp.${detail}.${verb}`, 'Bcp', 'bcmBcp', tab, producerSource,
    ])),
    ...['process', 'asset', 'vendor', 'recoveryResource', 'document', 'risk', 'bcp'].flatMap((detail) => ['linked', 'unlinked'].map((verb) => [
        `bia.${detail}.${verb}`, 'Bia', 'bcmBia', detail === 'process' ? 'relatedProcesses' : detail === 'asset' ? 'relatedAssets' : detail === 'vendor' ? 'relatedVendors' : detail === 'recoveryResource' ? 'recoveryResources' : detail === 'document' ? 'documents' : detail === 'risk' ? 'relatedRisks' : 'relatedBcps',
    ])),
    ...[
        ['communication', 'communications'],
        ['team', 'overview'],
        ['bcp', 'activatedBcps'],
        ['lessonLearned', 'learnImprove'],
        ['improvementAction', 'learnImprove'],
    ].flatMap(([detail, tab]) => ['linked', 'unlinked'].map((verb) => [
        `crisisEvent.${detail}.${verb}`, 'CrisisEvent', 'bcmCrisisEvent', tab,
    ])),
    ...['bcp', 'vendor', 'participant', 'assessment', 'timeline', 'recoveryActivity', 'evaluation', 'improvement', 'team', 'lessonLearned'].flatMap((detail) => ['linked', 'unlinked'].map((verb) => [
        `exercise.${detail}.${verb}`, 'Exercise', 'bcmExercises', detail === 'assessment' || detail === 'timeline' ? 'scenario' : detail === 'recoveryActivity' ? 'during' : detail === 'evaluation' || detail === 'improvement' || detail === 'lessonLearned' ? 'post-exercise' : 'overview',
    ])),
    ['riskRegister.biasLinked', 'RiskRegister', 'riskOperationsRiskRegister', 'relatedBias'],
    ['riskRegister.biaUnlinked', 'RiskRegister', 'riskOperationsRiskRegister', 'relatedBias'],
    ['riskRegister.objectivesLinked', 'RiskRegister', 'riskOperationsRiskRegister', 'relatedObjectives'],
    ['riskRegister.objectiveUnlinked', 'RiskRegister', 'riskOperationsRiskRegister', 'relatedObjectives'],
    ...[
        ['risk', 'objectiveRisks'],
        ['treatment', 'objectiveRisksTreatments'],
        ['kpi', 'kpis'],
        ['initiative', 'strategicInitiatives'],
    ].flatMap(([detail, tab]) => ['linked', 'unlinked'].map((verb) => [
        `objective.${detail}.${verb}`, 'Objective', 'isoObjective', tab,
    ])),
]);

const CUSTOM_POLICY_ROWS = Object.freeze([
    ['dataSubjectRequest.applicantReceipt', 'DataSubjectRequest', 'gdprDataSubjectRequest', 'apps/api/src/controllers/data-subject-request-public.controller.js:createPublicDataSubjectRequest'],
    ['verbis.pending', 'KvkkCompany', 'gdprVerbis', 'apps/api/src/listeners/verbis-change-tracker.listener.js'],
    ['disposalRun.completed', 'DisposalRun', 'gdprDisposal', 'apps/api/src/controllers/disposal.controller.js'],
    ['dpiaAssessment.completed', 'DpiaAssessment', 'gdprDpiaAssessment', 'apps/api/src/controllers/dpia-assessment.controller.js'],
    ['dpiaAssessment.submitted', 'DpiaAssessment', 'gdprDpiaAssessment', 'apps/api/src/controllers/dpia-assessment.controller.js'],
    ['privacyNotice.submitted', 'PrivacyNotice', 'gdprPrivacyNotice', 'apps/api/src/controllers/privacy-notice.controller.js'],
    ['privacyNotice.published', 'PrivacyNotice', 'gdprPrivacyNotice', 'apps/api/src/controllers/privacy-notice.controller.js'],
    ['matrixConfiguration.updated', 'MatrixConfiguration', 'definitionsScaleRiskAssessmentMatrix', 'apps/api/src/controllers/matrix-configuration.controller.js'],
    ['biaImpactConfiguration.updated', 'BiaImpactConfiguration', 'definitionsScaleBiaImpact', 'apps/api/src/controllers/bia-impact-configuration.controller.js'],
    ['delegation.created', 'Delegation', 'userOperationsDelegations', 'packages/delegation/src/delegation-service.js:createDelegation'],
    ['delegation.revoked', 'Delegation', 'userOperationsDelegations', 'packages/delegation/src/delegation-service.js:revokeDelegation'],
    ['delegation.declined', 'Delegation', 'userOperationsDelegations', 'packages/delegation/src/delegation-service.js:declineDelegation'],
    ['delegation.expired', 'Delegation', 'userOperationsDelegations', 'packages/delegation/src/delegation-service.js:expireEndedDelegations'],
    ['assetLicense.expiryReminder', 'AssetLicense', 'assetInventoryLicenseManagement', 'apps/api/src/lib/asset/asset-lifecycle-scheduler.js:scheduleLicenseExpiryReminder'],
    ['assetDisposal.plannedReminder', 'AssetDisposal', 'assetInventoryDisposalManagement', 'apps/api/src/lib/asset/asset-lifecycle-scheduler.js:scheduleDisposalPlannedReminder'],
    ['asset.supportEndReminder', 'Asset', 'assetInventoryAssets', 'apps/api/src/lib/asset/asset-lifecycle-scheduler.js:scheduleAssetSupportEndReminder'],
    ['vendor.contractExpiryReminder', 'Vendor', 'vendorManagementVendors', 'apps/api/src/lib/asset/asset-lifecycle-scheduler.js:scheduleVendorContractExpiryReminder'],
    ['vendorAssessment.reviewReminder', 'VendorAssessment', 'vendorManagementAssessments', 'apps/api/src/lib/asset/asset-lifecycle-scheduler.js:scheduleVendorAssessmentReviewReminder'],
    ['user.registered', 'User', 'userOperationsUserManagement', 'apps/api/src/controllers/auth.controller.js'],
    ['user.invited', 'User', 'userOperationsUserManagement', 'apps/api/src/controllers/user.controller.js'],
    ['user.passwordChanged', 'User', 'userOperationsUserManagement', 'apps/api/src/controllers/auth.controller.js;apps/api/src/controllers/user.controller.js'],
    ['auth.login', 'User', 'userOperationsUserManagement', 'apps/api/src/controllers/auth.controller.js'],
    ['problem.candidateRaised', 'ProblemCandidate', 'itsmProblem', 'apps/api/src/services/itsm/problem-detection.service.js:emitRaised'],
]);

function modelName(entity) {
    return entity.charAt(0).toUpperCase() + entity.slice(1);
}

function policyRow(name, family, extra = {}) {
    const entity = name.split('.')[0];
    const producerSources = {
        biaRpoCalculation: 'apps/api/src/controllers/rpo-calculation.controller.js:createBaseController',
        biaRpoRecommendation: 'apps/api/src/controllers/rpo-recommendation.controller.js:createBaseController',
        'workflow.task.assigned': 'packages/workflow-engine/src/bridges/task-assigned-emit.js:emitWorkflowTaskAssigned',
        'workflow.task.slaWarning': 'packages/scheduled-jobs/src/workflow-task-sla.js:checkWorkflowTaskSla',
        'workflow.task.slaBreached': 'packages/scheduled-jobs/src/workflow-task-sla.js:checkWorkflowTaskSla',
        'workflow.task.slaEscalated': 'packages/scheduled-jobs/src/workflow-task-sla.js:checkWorkflowTaskSla',
        'dataSubjectRequest.deadlineReminder': 'packages/scheduled-jobs/src/dsr-sla-scanner.js:scanDsrSla',
        'dataSubjectRequest.deadlineOverdue': 'packages/scheduled-jobs/src/dsr-sla-scanner.js:scanDsrSla',
        'dataBreach.deadlineReminder': 'packages/scheduled-jobs/src/breach-notification-scanner.js:scanBreachNotificationSla',
        'dataBreach.deadlineOverdue': 'packages/scheduled-jobs/src/breach-notification-scanner.js:scanBreachNotificationSla',
        'dataBreach.dataSubjectDeadlineReminder': 'packages/scheduled-jobs/src/breach-notification-scanner.js:scanBreachDataSubjectNotificationSla',
        'dataBreach.dataSubjectDeadlineOverdue': 'packages/scheduled-jobs/src/breach-notification-scanner.js:scanBreachDataSubjectNotificationSla',
        'disposalRun.created': 'apps/api/src/controllers/disposal.controller.js;packages/scheduled-jobs/src/disposal-run-create.js:attachPartyAndEmitDisposalRunCreated',
        'disposalRun.periodicDue': 'packages/scheduled-jobs/src/disposal-scanner.js:scanRetentionDisposal',
        'disposalRun.recordsExpired': 'packages/scheduled-jobs/src/disposal-scanner.js:scanRetentionDisposal',
        'dataProcessingAgreement.deadlineReminder': 'packages/scheduled-jobs/src/dpa-expiry-scanner.js:scanDpaExpiry',
        'dataProcessingAgreement.expired': 'packages/scheduled-jobs/src/dpa-expiry-scanner.js:scanDpaExpiry',
        'dataProcessingAgreement.subProcessorAdded': 'apps/api/src/controllers/data-processing-agreement.controller.js:addSubProcessor',
        'dataProcessingAgreement.subProcessorDecided': 'apps/api/src/controllers/data-processing-agreement.controller.js:decideSubProcessor',
        'gdpr.dataBreach.notified': 'apps/api/src/controllers/data-breach.controller.js:update',
        'internationalTransfer.sscDeadlineReminder': 'packages/scheduled-jobs/src/ssc-notification-scanner.js:scanSscNotificationSla',
        'internationalTransfer.sscDeadlineOverdue': 'packages/scheduled-jobs/src/ssc-notification-scanner.js:scanSscNotificationSla',
        'ticket.pendingDeleteApproved': 'apps/api/src/workflow/entity-lifecycle/ticket/pending-operation-executor.js:applyTicketPendingOperationFromInstance',
        'ticket.pendingDeleteRejected': 'apps/api/src/workflow/entity-lifecycle/ticket/pending-operation-executor.js:applyTicketPendingOperationFromInstance',
        'ticket.pendingStatusRejected': 'apps/api/src/workflow/entity-lifecycle/ticket/pending-operation-executor.js:applyTicketPendingOperationFromInstance',
        'incident.pendingDeleteApproved': 'apps/api/src/workflow/entity-lifecycle/incident/pending-operation-executor.js:applyIncidentPendingOperationFromInstance',
        'incident.pendingDeleteRejected': 'apps/api/src/workflow/entity-lifecycle/incident/pending-operation-executor.js:applyIncidentPendingOperationFromInstance',
        'incident.pendingStatusRejected': 'apps/api/src/workflow/entity-lifecycle/incident/pending-operation-executor.js:applyIncidentPendingOperationFromInstance',
        'asset.pendingDeleteApproved': 'apps/api/src/workflow/entity-lifecycle/asset/pending-operation-executor.js:applyAssetPendingOperationFromInstance',
        'asset.pendingDeleteRejected': 'apps/api/src/workflow/entity-lifecycle/asset/pending-operation-executor.js:applyAssetPendingOperationFromInstance',
        'formTask.assigned': 'packages/scheduled-jobs/src/survey-dispatch-execute.js:executeSurveyDispatch;apps/api/src/controllers/form-task.controller.js:assign',
        'collectionAssignment.assigned': 'packages/scheduled-jobs/src/collection-campaign-launch.js:processCollectionCampaignLaunches',
        'collectionAssignment.completed': 'apps/api/src/services/collection/collection-progress.js:refreshAssignmentContributions',
        'collectionAssignment.cancelled': 'apps/api/src/services/collection/collection-campaign.service.js:cancelCollectionCampaign',
        'collectionAssignment.reopened': 'apps/api/src/services/collection/collection-progress.js:refreshAssignmentContributions',
        'collectionAssignment.reminded': 'apps/api/src/services/collection/collection-campaign.service.js:remindCollectionCampaign',
        'collectionAssignment.deadlineWarning': 'packages/scheduled-jobs/src/collection-assignment-deadline-scanner.js:scanCollectionAssignmentDeadlines',
        'collectionAssignment.overdue': 'packages/scheduled-jobs/src/collection-assignment-deadline-scanner.js:scanCollectionAssignmentDeadlines',
        'collectionCampaign.launched': 'packages/scheduled-jobs/src/collection-campaign-launch.js:processCollectionCampaignLaunches',
        'collectionCampaign.cancelled': 'apps/api/src/services/collection/collection-campaign.service.js:cancelCollectionCampaign',
        'workflow.informed.assigned': 'packages/workflow-engine/src/bridges/informed-assigned-emit.js:emitWorkflowInformedAssigned',
        'role.assigned': 'apps/api/src/controllers/role.controller.js:assignUsers',
        'role.permissionsChanged': 'apps/api/src/controllers/role.controller.js:updateRole',
        'ticket.assigned': 'apps/api/src/controllers/ticket.controller.js:assign',
        'ticket.resolved': 'apps/api/src/controllers/ticket.controller.js:resolve',
        'ticket.closed': 'apps/api/src/controllers/ticket.controller.js:close',
        'ticket.submitted': 'apps/api/src/controllers/ticket.controller.js:submit',
        'ticket.cancelled': 'apps/api/src/controllers/ticket.controller.js:cancel',
        'ticket.approved': 'apps/api/src/listeners/ticket-workflow.listener.js:applyTicketStatusFromInstance',
        'ticket.rejected': 'apps/api/src/listeners/ticket-workflow.listener.js:applyTicketStatusFromInstance',
        'ticket.returned': 'apps/api/src/listeners/ticket-workflow.listener.js:applyTicketStatusFromInstance',
        'ticket.delegated': 'packages/workflow-engine/src/bridges/task-assigned-emit.js:emitWorkflowTaskAssigned',
        'ticket.slaBreached': 'packages/scheduled-jobs/src/ticket-sla-breach.js:checkSlaBreaches',
        'ticket.slaWarning': 'packages/scheduled-jobs/src/ticket-sla-warning.js:checkSlaWarnings',
        'ticket.escalated': 'packages/scheduled-jobs/src/ticket-sla-escalation.js:checkSlaEscalations',
        'ticket.commented': 'apps/api/src/controllers/ticket-comment.controller.js:afterCreate',
        'bia.submitted': 'apps/api/src/controllers/bia.controller.js:submit',
        'bia.completed': 'apps/api/src/lib/bcm/bcm-approval.js:applyBcmWorkflowState',
        'bcp.submitted': 'apps/api/src/controllers/bcp.controller.js:submit',
        'bcp.activated': 'apps/api/src/lib/bcm/bcp-activation.js:activateBcpOnCrisis',
        'crisisEvent.declared': 'apps/api/src/controllers/crisis-event.controller.js:afterUpdate',
        'crisisEvent.resolved': 'apps/api/src/controllers/crisis-event.controller.js:afterUpdate',
        'incident.submitted': 'apps/api/src/controllers/incident-lifecycle.controller.js:submit',
        'incident.cancelled': 'apps/api/src/workflow/entity-lifecycle/incident/lifecycle.service.js:transitionIncidentStatus',
        'incident.assigned': 'apps/api/src/controllers/incident-lifecycle.controller.js:assign',
        'incident.resolved': 'apps/api/src/controllers/incident-lifecycle.controller.js:resolve',
        'incident.closed': 'apps/api/src/controllers/incident-lifecycle.controller.js:close',
        'incident.approved': 'apps/api/src/listeners/incident-workflow.listener.js:applyIncidentStatusFromInstance',
        'incident.rejected': 'apps/api/src/listeners/incident-workflow.listener.js:applyIncidentStatusFromInstance',
        'incident.returned': 'apps/api/src/listeners/incident-workflow.listener.js:applyIncidentStatusFromInstance',
        'incident.delegated': 'packages/workflow-engine/src/bridges/task-assigned-emit.js:emitWorkflowTaskAssigned',
        'incident.slaBreached': 'packages/scheduled-jobs/src/incident-sla-breach.js:checkIncidentSlaBreaches',
        'training.assigned': 'apps/api/src/lib/training/training-lifecycle-events.js:emitTrainingAssigned',
        'training.completed': 'apps/api/src/lib/training/training-lifecycle-events.js:emitTrainingCompletedIfNeeded',
        'training.overdue': 'packages/scheduled-jobs/src/training-overdue-scanner.js:scanTrainingOverdue',
        'operationalRun.started': 'apps/api/src/services/bcm/operational-run.service.js:startOperationalRun',
        'operationalRun.paused': 'apps/api/src/services/bcm/operational-run.service.js:transitionRun',
        'operationalRun.resumed': 'apps/api/src/services/bcm/operational-run.service.js:transitionRun',
        'operationalRun.completed': 'apps/api/src/services/bcm/operational-run.service.js:transitionRun',
        'operationalRun.cancelled': 'apps/api/src/services/bcm/operational-run.service.js:transitionRun',
        'operationalRun.update.appended': 'apps/api/src/services/bcm/operational-run.service.js:appendOperationalRunUpdate',
        'operationalRun.task.assigned': 'apps/api/src/services/bcm/operational-run.service.js:startOperationalRun',
        'operationalRun.task.started': 'apps/api/src/services/bcm/operational-run.service.js:startOwnOperationalTask',
        'operationalRun.task.completed': 'apps/api/src/services/bcm/operational-run.service.js:completeOwnOperationalTask',
        'operationalRun.task.cancelled': 'apps/api/src/services/bcm/operational-run.service.js:cancelUnfinishedTasks',
        'operationalRun.task.reassigned': 'apps/api/src/services/bcm/operational-run.service.js:reassignOperationalTask',
        'operationalRun.task.evidenceUploaded': 'apps/api/src/services/bcm/operational-run-evidence.service.js:uploadOperationalTaskEvidence',
        'operationalRun.task.dependenciesOverridden': 'apps/api/src/services/bcm/operational-run.service.js:overrideOperationalTaskDependencies',
        'operationalRun.task.deadlineWarning': 'packages/scheduled-jobs/src/operational-run-deadline-scanner.js:scanOperationalRunDeadlines',
        'operationalRun.task.overdue': 'packages/scheduled-jobs/src/operational-run-deadline-scanner.js:scanOperationalRunDeadlines',
    };
    return Object.freeze({
        event: name,
        family,
        owner: 'policy',
        resourceModel: extra.resourceModel || (entity === 'tenantDirectory' ? 'TenantDirectoryConfig' : modelName(entity)),
        pageKey: extra.pageKey || CRUD_PAGE_KEYS[entity] || null,
        definitionProfile: extra.definitionProfile || (family === 'crud' ? 'crud.v1' : 'specialty.v1'),
        producerKind: extra.producerKind || (family === 'crud' ? 'baseController' : 'explicit'),
        producerSource: extra.producerSource
            || producerSources[name]
            || producerSources[entity]
            || (family === 'crud' ? 'apps/api/src/controllers/base.controller.js:createBaseController' : null),
        adapterProfile: extra.adapterProfile || (family === 'crud' ? `crud.${entity}.v1` : `${entity}.lifecycle.v1`),
        guardProfile: extra.guardProfile || (entity === 'asset' ? 'asset-record-access.v1' : 'page-view.v1'),
        audienceProfile: extra.audienceProfile || (family === 'crud' ? 'pageViewers' : 'eventRelation'),
        displayNameFields: Object.freeze(extra.displayNameFields || ['name']),
        safeFields: Object.freeze(extra.safeFields || ['name']),
        fixture: Object.freeze(extra.fixture || { resourceId: `fixture-${entity}` }),
        ...(extra.detail ? { detail: Object.freeze(extra.detail) } : {}),
        aliases: Object.freeze(extra.aliases || []),
    });
}

export const POLICY_OWNERSHIP_ROWS = Object.freeze([
    ...CRUD_ENTITIES.flatMap((entity) => ['created', 'updated', 'deleted'].map((verb) => (
        policyRow(`${entity}.${verb}`, 'crud', {
            pageKey: CRUD_PAGE_KEYS[entity],
            guardProfile: entity === 'asset' ? 'asset-record-access.v1' : 'page-view.v1',
            ...(entity === 'action' ? { displayNameFields: ['title', 'name'], safeFields: ['name', 'actionType'] } : {}),
            ...(entity === 'threat' ? { displayNameFields: ['threatName'] } : {}),
        })
    ))),
    ...SPECIALTY_POLICY_EVENTS.map((event) => policyRow(event, 'specialty', {
        ...(event.startsWith('operationalRun.task.')
            ? {
                resourceModel: 'OperationalRunTask',
                pageKey: 'myWork',
                adapterProfile: 'operationalRun.task.v1',
                guardProfile: 'relation-only.v1',
                displayNameFields: ['title'],
                safeFields: ['title', 'dueAt', 'status'],
            }
            : {}),
        ...(event.startsWith('operationalRun.') && !event.startsWith('operationalRun.task.')
            ? {
                resourceModel: 'OperationalRun',
                pageKey: 'myWork',
                adapterProfile: 'operationalRun.lifecycle.v1',
                guardProfile: 'relation-only.v1',
                displayNameFields: ['sourceTitle', 'code'],
                safeFields: ['sourceTitle', 'code', 'sourceType', 'startedAt'],
            }
            : {}),
        ...(event === 'formTask.assigned'
            ? {
                resourceModel: 'FormTask',
                pageKey: 'myWork',
                adapterProfile: 'formTask.assignment.v1',
                guardProfile: 'relation-only.v1',
                displayNameFields: ['title'],
                safeFields: ['title'],
            }
            : {}),
        ...(event.startsWith('collectionAssignment.')
            ? {
                resourceModel: 'CollectionAssignment',
                pageKey: 'myWork',
                adapterProfile: 'collectionAssignment.assignment.v1',
                guardProfile: 'relation-only.v1',
                displayNameFields: ['title'],
                safeFields: ['title', 'entityType'],
            }
            : {}),
        ...(event.startsWith('collectionCampaign.')
            ? {
                resourceModel: 'CollectionCampaign',
                pageKey: 'collectionCampaigns',
                adapterProfile: 'collectionCampaign.lifecycle.v1',
                guardProfile: 'relation-only.v1',
                displayNameFields: ['name'],
                safeFields: ['name'],
            }
            : {}),
        ...(event === 'gdpr.dataBreach.notified'
            ? {
                resourceModel: 'DataBreach',
                pageKey: 'gdprDataBreach',
                adapterProfile: 'dataBreach.lifecycle.v1',
                displayNameFields: ['referenceNumber', 'name'],
                safeFields: ['referenceNumber', 'name'],
            }
            : {}),
        ...(event === 'dataProcessingAgreement.subProcessorAdded' || event === 'dataProcessingAgreement.subProcessorDecided'
            ? {
                displayNameFields: ['code', 'name'],
                safeFields: event.endsWith('Decided')
                    ? ['code', 'name', 'subProcessorName', 'decision']
                    : ['code', 'name', 'subProcessorName'],
            }
            : {}),
    })),
    ...CUSTOM_POLICY_ROWS.map(([event, resourceModel, pageKey, producerSource]) => policyRow(event, 'custom', {
        resourceModel, pageKey, producerKind: 'explicit', producerSource,
    })),
    ...DETAIL_FAMILY_ROWS.map(([event, resourceModel, pageKey, tab, producerSource]) => policyRow(event, 'detail', {
        resourceModel, pageKey, definitionProfile: 'detail.v1', producerKind: 'explicit',
        producerSource: producerSource || DETAIL_PRODUCER_SOURCES[resourceModel],
        adapterProfile: 'detail-association.v1',
        guardProfile: resourceModel === 'Asset' ? 'asset-record-access.v1' : 'page-view.v1', audienceProfile: 'pageViewers',
        displayNameFields: ['name', 'title', 'code'], safeFields: ['name'],
        detail: {
            tab,
            maxLinkedIds: 500,
            ...(['Bcp', 'Bia', 'CrisisEvent', 'Exercise'].includes(resourceModel)
                ? { roleHolderRecipients: true }
                : {}),
        },
    })),
]);

export const POLICY_OWNERSHIP_EVENT_NAMES = Object.freeze(POLICY_OWNERSHIP_ROWS.map(({ event }) => event));
export const POLICY_OWNERSHIP_EVENT_SET = new Set(POLICY_OWNERSHIP_EVENT_NAMES);

export const CODE_OWNED_NOTIFICATION_RULES = Object.freeze(
    CODE_OWNED_EXACT.map((event) => Object.freeze({ event, reason: 'Application/security/system delivery is code-owned by contract.' })),
);

export const CATALOG_ONLY_NOTIFICATION_RULES = Object.freeze(
    CATALOG_ONLY_EXACT.map((event) => Object.freeze({ event, reason: 'Catalog entry has no verified live producer/delivery intersection.' })),
);
export const POLICY_BLOCKED_NOTIFICATION_RULES = Object.freeze(
    POLICY_BLOCKED_EXACT.map((event) => Object.freeze({ event, reason: 'Live delivery subscription exists, but no strict durable producer exists yet.' })),
);

function ruleFor(eventName) {
    const canonical = toCanonicalEventName(eventName);
    if (POLICY_OWNERSHIP_EVENT_SET.has(canonical)) {
        return { owner: 'policy', reason: 'Explicit policy ownership row with managed definition and compatibility owner handoff.' };
    }
    const exactCode = CODE_OWNED_NOTIFICATION_RULES.find((rule) => rule.event === canonical);
    if (exactCode) return { owner: 'code-owned', reason: exactCode.reason };
    const catalogOnly = CATALOG_ONLY_NOTIFICATION_RULES.find((rule) => rule.event === canonical);
    if (catalogOnly) return { owner: 'catalog-only', reason: catalogOnly.reason };
    const blocked = POLICY_BLOCKED_NOTIFICATION_RULES.find((rule) => rule.event === canonical);
    if (blocked) return { owner: 'policy-blocked', reason: blocked.reason };
    return null;
}

/** Classify an inventory row without allowing source evidence to promote it. */
export function classifyNotificationOwnership({ event, liveProducer = false, liveDelivery = false } = {}) {
    const canonical = toCanonicalEventName(event);
    const explicit = ruleFor(canonical);
    if (explicit) return { event: canonical, ...explicit };
    // A producer is not a notification owner. Catalog-only is the safe
    // disposition until a real delivery path is proven. The inverse is a
    // dead delivery subscription and remains policy-blocked for follow-up.
    if (!liveDelivery) {
        return { event: canonical, owner: 'catalog-only', reason: 'No live notification delivery owner was found by the source inventory.' };
    }
    return {
        event: canonical,
        owner: 'policy-blocked',
        reason: liveProducer
            ? 'Live producer and notification delivery owner exist, but no explicit managed contract or code-owned exception exists.'
            : 'Live notification delivery owner has no verified producer.',
    };
}

export function getNotificationOwnership(eventName, evidence = {}) {
    const row = classifyNotificationOwnership({ event: eventName, ...evidence });
    return row;
}

export function listNotificationOwnershipRows() {
    return [...POLICY_OWNERSHIP_ROWS];
}

export function listPolicyOwnedCrudResourceRows() {
    return POLICY_OWNERSHIP_ROWS
        .filter((row) => row.family === 'crud')
        .filter((row, index, rows) => rows.findIndex((candidate) => candidate.resourceModel === row.resourceModel) === index)
        .map((row) => ({ ...row }));
}

export function listPolicyOwnedCustomResourceRows() {
    return POLICY_OWNERSHIP_ROWS.filter((row) => row.family === 'custom').map((row) => ({ ...row }));
}

export function listPolicyOwnedNotificationEventNames() {
    return [...POLICY_OWNERSHIP_EVENT_NAMES];
}

export function isPolicyOwnedNotificationEvent(eventName) {
    return POLICY_OWNERSHIP_EVENT_SET.has(toCanonicalEventName(eventName));
}

export function assertNotificationOwnershipRegistry() {
    const names = POLICY_OWNERSHIP_EVENT_NAMES;
    if (new Set(names).size !== names.length) throw new Error('Duplicate notification ownership event');
    for (const row of POLICY_OWNERSHIP_ROWS) {
        if (!getEventDef(row.event)) throw new Error(`Ownership event is absent from catalog: ${row.event}`);
        if (!NOTIFICATION_OWNERS.includes(row.owner)) throw new Error(`Unknown notification owner: ${row.owner}`);
    }
    return true;
}

assertNotificationOwnershipRegistry();
